import {
  Flex,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tag,
  TagCloseButton,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { Info } from '@Components/Common/Info';
import { validateEmail } from '@Utils/validate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type AutoCollectSourceType = 'Microsoft' | 'Other';

export const MAX_AUTO_COLLECT_MAILBOXES = 3;

interface AutoCollectSectionProps {
  /** Master parent toggle (Report to TrendAI). Section is fully disabled when off. */
  parentEnabled: boolean;
  /** Whether auto-collect itself is enabled. */
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  /** Source type applied to all mailboxes. */
  sourceType: AutoCollectSourceType;
  onSourceTypeChange: (next: AutoCollectSourceType) => void;
  /** Configured mailboxes (max 3). */
  mailboxes: string[];
  onMailboxesChange: (next: string[]) => void;
  /**
   * Addresses the BFF flagged as not belonging to the org on the last save.
   * Cleared by the parent on any edit.
   */
  invalidMailboxes?: string[];
  /**
   * Mailboxes currently configured in the sibling "Report to administrators"
   * field. Used to detect cross-list collisions — a mailbox in both lists
   * would cause repeated auto-collection of GTOC notifications.
   */
  reportToAdminMailboxes?: string[];
  /**
   * Notifies the parent whenever the validity of this section changes so the
   * Save button can be disabled while auto-collect input is in error.
   */
  onErrorChange?: (hasError: boolean) => void;
}

/**
 * Inline chip input. Enter / comma / semicolon / blur commits the typed
 * value as a chip; chips share a border with the input.
 */
const MailboxChipInput = ({
  mailboxes,
  onMailboxesChange,
  disabled,
  capError,
  draft,
  setDraft,
  draftError,
  setDraftError,
  commit,
  removeMailbox,
  placeholder,
  removeLabel,
  invalidSet,
  collisionSet,
}: {
  mailboxes: string[];
  onMailboxesChange: (next: string[]) => void;
  disabled: boolean;
  capError: boolean;
  draft: string;
  setDraft: (v: string) => void;
  draftError?: string;
  setDraftError: (v: string | undefined) => void;
  commit: () => void;
  removeMailbox: (mb: string) => void;
  placeholder: string;
  removeLabel: string;
  invalidSet: Set<string>;
  collisionSet: Set<string>;
}) => {
  const showError =
    capError ||
    Boolean(draftError) ||
    invalidSet.size > 0 ||
    collisionSet.size > 0;
  return (
    <Flex
      alignItems="center"
      flexWrap="wrap"
      gap="1x"
      width="701px"
      minHeight="32px"
      borderWidth="1px"
      borderRadius="4px"
      borderStyle="solid"
      borderColor={showError ? '#c70721' : 'gray:60'}
      paddingX="3x"
      paddingY="1x"
      opacity={disabled ? 0.5 : 1}
    >
      {mailboxes.map((mb, idx) => {
        const isOverflow = capError && idx === mailboxes.length - 1;
        const isInvalid = invalidSet.has(mb);
        const isCollision = collisionSet.has(mb);
        const isRed = isOverflow || isInvalid || isCollision;
        return (
          <Tag
            key={mb}
            variant="solid"
            sx={{
              backgroundColor: isRed ? '#c70721' : 'rgba(140,140,140,0.16)',
              color: isRed ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.8)',
              borderRadius: '9999px',
              minHeight: '24px',
              maxHeight: '24px',
              paddingX: '8px',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0px',
            }}
          >
            {mb}
            <TagCloseButton
              aria-label={removeLabel}
              onClick={() => removeMailbox(mb)}
              disabled={disabled}
            />
          </Tag>
        );
      })}
      <Input
        id="autoCollectMailboxDraft"
        type="text"
        variant="unstyled"
        flex={1}
        minWidth="180px"
        placeholder={mailboxes.length === 0 ? placeholder : ''}
        value={draft}
        disabled={disabled}
        onChange={(e: any) => {
          setDraft(e.target.value);
          if (draftError) setDraftError(undefined);
        }}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
            e.preventDefault();
            commit();
            return;
          }
          if (
            e.key === 'Backspace' &&
            draft === '' &&
            mailboxes.length > 0 &&
            !disabled
          ) {
            e.preventDefault();
            onMailboxesChange(mailboxes.slice(0, -1));
          }
        }}
        onBlur={() => {
          if (draft.trim()) commit();
        }}
        maxLength={320}
      />
    </Flex>
  );
};

export const AutoCollectSection = ({
  parentEnabled,
  enabled,
  onEnabledChange,
  sourceType,
  onSourceTypeChange,
  mailboxes,
  onMailboxesChange,
  invalidMailboxes,
  reportToAdminMailboxes,
  onErrorChange,
}: AutoCollectSectionProps) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | undefined>(undefined);

  const sectionDisabled = !parentEnabled || !enabled;

  const commit = useCallback(() => {
    const value = draft.trim().toLowerCase();
    if (!value) return;
    if (!validateEmail(value)) {
      setDraftError(
        t('administration.addIn.autoCollect.mailboxes.invalidEmail')
      );
      return;
    }
    if (mailboxes.includes(value)) {
      setDraftError(t('administration.addIn.autoCollect.mailboxes.duplicate'));
      return;
    }
    if (mailboxes.length >= MAX_AUTO_COLLECT_MAILBOXES) {
      // Allow the value to drop in (so the cap-overflow chip is visible),
      // then surface the cap error inline. The save handler / API rejects.
      onMailboxesChange([...mailboxes, value]);
      setDraft('');
      setDraftError(undefined);
      return;
    }
    onMailboxesChange([...mailboxes, value]);
    setDraft('');
    setDraftError(undefined);
  }, [draft, mailboxes, onMailboxesChange, t]);

  const removeMailbox = useCallback(
    (mb: string) => {
      onMailboxesChange(mailboxes.filter((m) => m !== mb));
    },
    [mailboxes, onMailboxesChange]
  );

  const overCap = mailboxes.length > MAX_AUTO_COLLECT_MAILBOXES;
  const invalidSet = useMemo(
    () => new Set((invalidMailboxes ?? []).map((m) => m.toLowerCase())),
    [invalidMailboxes]
  );
  const hasInvalidMailbox = invalidSet.size > 0;

  // Cross-list collision: a mailbox added here that is also in the sibling
  // "Report to administrators" list would auto-collect its own GTOC
  // notifications. We block save until the user removes one side.
  const collisionSet = useMemo(() => {
    if (!reportToAdminMailboxes || reportToAdminMailboxes.length === 0) {
      return new Set<string>();
    }
    const adminLower = new Set(
      reportToAdminMailboxes.map((m) => m.trim().toLowerCase())
    );
    return new Set(
      mailboxes.map((m) => m.toLowerCase()).filter((m) => adminLower.has(m))
    );
  }, [mailboxes, reportToAdminMailboxes]);
  const hasCollision = collisionSet.size > 0;

  const hasError =
    enabled &&
    (overCap || hasInvalidMailbox || hasCollision || Boolean(draftError));

  // Bubble up validity changes so the parent can disable Save.
  useEffect(() => {
    onErrorChange?.(hasError);
  }, [hasError, onErrorChange]);

  return (
    <Stack gap="2x" ml={62}>
      {/* Sub-toggle row */}
      <Flex alignItems="center" columnGap="2x">
        <Switch
          id="autoCollectEnableStatus"
          checked={parentEnabled && enabled}
          disabled={!parentEnabled}
          onChange={(e: any) => onEnabledChange(e.target.checked)}
        />
        <Text size="md">{t('administration.addIn.autoCollect.title')}</Text>
        <Info
          content={t('administration.addIn.details.autoCollect.info')}
          placement="top-start"
        />
      </Flex>

      {/* Description paragraph (always shown when section visible) */}
      <Text color="text.secondary" ml="13x">
        {t('administration.addIn.autoCollect.description')}
      </Text>

      {/* Source-type radios — vertical stack, each option carries its own
          helper text underneath, no separate "Reporting source" label. */}
      <RadioGroup
        value={sourceType}
        onChange={(value: string) =>
          onSourceTypeChange(value as AutoCollectSourceType)
        }
        disabled={sectionDisabled}
      >
        <Stack gap="2x" ml="13x">
          <Stack gap="0x">
            <Radio value="Microsoft">
              {t('administration.addIn.autoCollect.sourceType.microsoft')}
            </Radio>
            <Text color="text.secondary" fontSize="sm" ml="6x">
              {t('administration.addIn.autoCollect.sourceType.microsoftHelper')}
            </Text>
          </Stack>
          <Stack gap="0x">
            <Radio value="Other">
              {t('administration.addIn.autoCollect.sourceType.other')}
            </Radio>
            <Text color="text.secondary" fontSize="sm" ml="6x">
              {t('administration.addIn.autoCollect.sourceType.otherHelper')}
            </Text>
          </Stack>
        </Stack>
      </RadioGroup>

      {/* Mailbox chip-input editor (max 3) */}
      <Stack gap="1x" ml="13x">
        <Text>
          {t('administration.addIn.autoCollect.mailboxes.label')}{' '}
          <Text as="span" color="text.secondary">
            ({mailboxes.length} / {MAX_AUTO_COLLECT_MAILBOXES})
          </Text>
        </Text>
        <MailboxChipInput
          mailboxes={mailboxes}
          onMailboxesChange={onMailboxesChange}
          disabled={sectionDisabled}
          capError={overCap}
          draft={draft}
          setDraft={setDraft}
          draftError={draftError}
          setDraftError={setDraftError}
          commit={commit}
          removeMailbox={removeMailbox}
          placeholder={t(
            'administration.addIn.autoCollect.mailboxes.placeholder'
          )}
          removeLabel={t('administration.addIn.autoCollect.mailboxes.remove')}
          invalidSet={invalidSet}
          collisionSet={collisionSet}
        />
        {/* Error priority: not-in-org > collision > cap > draft */}
        {hasInvalidMailbox ? (
          <Text color="#fe4648" fontSize="12px" lineHeight="16px">
            {t('administration.addIn.autoCollect.mailboxes.notInOrg')}
          </Text>
        ) : hasCollision ? (
          <Text color="#fe4648" fontSize="12px" lineHeight="16px">
            {t(
              'administration.addIn.autoCollect.mailboxes.collisionWithReportToAdmin'
            )}
          </Text>
        ) : overCap ? (
          <Text color="#fe4648" fontSize="12px" lineHeight="16px">
            {t('administration.addIn.autoCollect.mailboxes.cap')}
          </Text>
        ) : draftError ? (
          <Text color="#fe4648" fontSize="12px" lineHeight="16px">
            {draftError}
          </Text>
        ) : (
          <Text
            color={colorStyle.color.secondary}
            fontSize="12px"
            lineHeight="16px"
          >
            {t('administration.addIn.autoCollect.mailboxes.helper')}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
